import {
    useGetSubscriptionsStatus,
    useSubscribeToNotifications,
    useUnsubscribeFromNotifications,
} from '../api/notificationsApi';
import type { FirebaseContextProps } from '@/app/providers/firebaseProvider';
import { useFirebaseContext } from '@/app/providers/firebaseProvider';
import { appStorageController } from '@/app/storage';

interface UseNotificationsReturn {
    isLoading: boolean;
    api?: {
        toggleSubscription: () => void;
        isSubscribed: boolean;
    };
    browser?: Pick<typeof Notification, 'permission'> & Pick<FirebaseContextProps, 'registerFirebaseToken'>;
}

export const useNotifications = (): UseNotificationsReturn => {
    const { token: firebaseToken, isLoading: tokenIsLoading, registerFirebaseToken } = useFirebaseContext();
    const [ subscribe, { isLoading: subscribeRequestIsLoading } ] = useSubscribeToNotifications();
    const [ unsubscribe, { isLoading: unsubscribeRequestIsLoading } ] = useUnsubscribeFromNotifications();
    const {
        data: isSubscribed,
        isLoading: getSubscriptionStatusRequestIsLoading,
    } = useGetSubscriptionsStatus(appStorageController.get('subscriptionId') ?? '');

    const isLoading = tokenIsLoading
      || getSubscriptionStatusRequestIsLoading
      || subscribeRequestIsLoading
      || unsubscribeRequestIsLoading;

    const toggleSubscription = () => {
        if (isLoading || !firebaseToken) return;

        const subscriptionId = appStorageController.get('subscriptionId');

        isSubscribed && subscriptionId
            ? unsubscribe(subscriptionId ?? '')
            : subscribe({ token: firebaseToken });
    };

    const api: UseNotificationsReturn['api'] = {
        toggleSubscription,
        isSubscribed: Boolean(localStorage.getItem('subscriptionId')) && Boolean(isSubscribed),
    };

    return {
        api,
        isLoading,
        browser: window.Notification
            ? {
                permission: Notification.permission,
                registerFirebaseToken,
            }
            : undefined,
    };
};
